import type {
  AssistantAppearance,
  AssistantBorderRadius,
  AssistantFont,
  AssistantIcon,
  AssistantPosition,
} from "../types";
import type { IconName } from "./icons";

const BUTTON_SIZE_PX = 56;
const BUTTON_GAP_PX = 12;
const DEFAULT_OFFSET_PX = 24;
const MAX_OFFSET_PX = 200;
const PANEL_GUTTER_PX = 16;
const PANEL_WIDTH_PX = 380;

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const DEFAULT_COLORS = { primary: "#2563EB", background: "#FFFFFF", text: "#111827" };

// Mesmos valores do painel (assistant-agent-preview.tsx): pouco, médio, muito.
const RADII: Record<AssistantBorderRadius, string> = {
  small: "0.5rem",
  medium: "1rem",
  large: "1.5rem",
};
const FONTS: Record<AssistantFont, string> = {
  system: "system-ui, -apple-system, sans-serif",
  inter: "Inter, system-ui, sans-serif",
  roboto: "Roboto, system-ui, sans-serif",
};
// Mesmo mapeamento do painel: headset usa o ícone de fone (Headphones) como na prévia.
const ICONS: Record<AssistantIcon, IconName> = {
  sparkles: "sparkles",
  headset: "headphones",
  "shopping-bag": "shopping-bag",
};

export interface ResolvedAppearance {
  primary: string;
  background: string;
  text: string;
  radius: string;
  font: string;
  icon: IconName;
  iconUrl: string | null;
  anchor: AssistantPosition["anchor"];
  offsetX: number;
  offsetY: number;
}

function pick<T extends string, V extends string>(map: Record<T, V>, key: unknown, fallback: T): V {
  const known = typeof key === "string" && (Object.keys(map) as string[]).includes(key);
  return known ? map[key as T] : map[fallback];
}

export function toHexColor(value: unknown, fallback: string): string {
  return typeof value === "string" && HEX_COLOR.test(value) ? value : fallback;
}

export function toOffset(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULT_OFFSET_PX;
  return Math.min(MAX_OFFSET_PX, Math.max(0, Math.round(value)));
}

// Só https: evita javascript:, http: (conteúdo misto) e URLs malformadas.
export function toHttpsUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    return new URL(value).protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

// Mesma regra do painel (getAssistantAgentContrastColor): texto escuro sobre cor clara, branco sobre escura.
function linearChannel(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

export function contrastColor(hex: string): "#111827" | "#FFFFFF" {
  const digits = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(digits)) return "#FFFFFF";
  const r = linearChannel(Number.parseInt(digits.slice(0, 2), 16) / 255);
  const g = linearChannel(Number.parseInt(digits.slice(2, 4), 16) / 255);
  const b = linearChannel(Number.parseInt(digits.slice(4, 6), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.179 ? "#111827" : "#FFFFFF";
}

// Painel fica do mesmo lado do botão, mas nunca sai da tela: no celular o offset
// grande empurraria a janela (380px) para fora. Limitado a [16px, espaço que sobra].
export function panelOffsetCss(offsetX: number): string {
  return `clamp(${PANEL_GUTTER_PX}px, ${offsetX}px, calc(100vw - min(${PANEL_WIDTH_PX}px, 100vw - ${PANEL_GUTTER_PX * 2}px) - ${PANEL_GUTTER_PX}px))`;
}

// Painel fica logo acima do botão (botão + espaçamento).
export function panelBottomPx(offsetY: number): number {
  return offsetY + BUTTON_SIZE_PX + BUTTON_GAP_PX;
}

export function resolveAppearance(
  appearance: Partial<AssistantAppearance> | null,
): ResolvedAppearance {
  const look = appearance ?? {};
  const position: Partial<AssistantPosition> = look.position ?? {};

  return {
    primary: toHexColor(look.primaryColor, DEFAULT_COLORS.primary),
    background: toHexColor(look.backgroundColor, DEFAULT_COLORS.background),
    text: toHexColor(look.textColor, DEFAULT_COLORS.text),
    radius: pick(RADII, look.borderRadius, "medium"),
    font: pick(FONTS, look.font, "system"),
    icon: pick(ICONS, look.icon, "sparkles"),
    iconUrl: toHttpsUrl(look.customIconUrl),
    anchor: position.anchor === "bottom-left" ? "bottom-left" : "bottom-right",
    offsetX: toOffset(position.offsetX),
    offsetY: toOffset(position.offsetY),
  };
}
